import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { kbAPI } from '../../services/api';
import { Search, Loader2, BookOpen, ChevronRight, Tag } from 'lucide-react';
import { Input } from '../../components/ui/input';

const KnowledgeBasePage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          kbAPI.listArticles({ category: selectedCategory }),
          kbAPI.getCategories(),
        ]);
        setArticles(articlesRes.data || []);
        setCategories(categoriesRes.data || []);
      } catch (err) {
        setArticles([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const filteredArticles = articles.filter(a => {
    if (!search.trim()) return true;
    const title = a.title?.de || a.title?.en || '';
    return title.toLowerCase().includes(search.toLowerCase());
  });

  if (selectedArticle) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto" data-testid="kb-article-view">
          <button
            onClick={() => setSelectedArticle(null)}
            className="text-sm text-slate-400 hover:text-white mb-6 flex items-center gap-1 transition-colors"
            data-testid="kb-back-btn"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Zurück zur Übersicht
          </button>
          <article className="glass-card p-8">
            <div className="flex items-center gap-2 mb-4">
              {selectedArticle.category && (
                <span className="badge-neutral text-xs">{selectedArticle.category}</span>
              )}
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-6">
              {selectedArticle.title?.de || selectedArticle.title?.en}
            </h1>
            <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed whitespace-pre-wrap">
              {selectedArticle.content?.de || selectedArticle.content?.en || 'Kein Inhalt verfügbar.'}
            </div>
          </article>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="kb-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-white">Wissensdatenbank</h1>
          <p className="text-slate-400 mt-1 text-sm">Anleitungen, Tutorials und technische Dokumentation</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Artikel durchsuchen..."
              className="input-field pl-10"
              data-testid="kb-search-input"
            />
          </div>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!selectedCategory ? 'bg-[#EC2229] text-white' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'}`}
              data-testid="kb-category-all"
            >
              Alle
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedCategory === cat ? 'bg-[#EC2229] text-white' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'}`}
                data-testid={`kb-category-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
          </div>
        )}

        {!loading && filteredArticles.length === 0 && (
          <div className="glass-card p-8 text-center">
            <BookOpen className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Keine Artikel gefunden</p>
            <p className="text-sm text-slate-400">
              {articles.length === 0 
                ? 'Die Wissensdatenbank wird derzeit aufgebaut. Schauen Sie bald wieder vorbei!'
                : 'Versuchen Sie einen anderen Suchbegriff oder eine andere Kategorie.'}
            </p>
          </div>
        )}

        {!loading && filteredArticles.length > 0 && (
          <div className="space-y-3" data-testid="kb-articles-list">
            {filteredArticles.map((article, i) => (
              <button
                key={i}
                onClick={() => setSelectedArticle(article)}
                className="w-full text-left glass-card p-5 hover:border-[#EC2229]/20 transition-colors group"
                data-testid={`kb-article-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {article.category && (
                        <span className="badge-neutral text-xs">{article.category}</span>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold text-white group-hover:text-[#EC2229] transition-colors">
                      {article.title?.de || article.title?.en}
                    </h3>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-[#EC2229] transition-colors flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default KnowledgeBasePage;
